import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/I18nContext";

/**
 * Phase 18 — ASL Feedback Border.
 *
 * Full-viewport fixed overlay that pulses a 6-px inset border on
 * receiving a `fm:asl_feedback` window event. Green (#39FF14) for
 * success, crimson (#DC143C) for error. 800 ms total.
 *
 * ONLY renders when the user is in ASL accessibility mode. This is
 * the single motion exception to the ASL motion-reduce sweep — see
 * `styles/a11y-asl.css` for the explicit `.asl-feedback-pulse`
 * override that keeps the animation alive under `data-a11y-mode="asl"`.
 *
 * Non-ASL users: no border ever renders (regression check #10 in the
 * Phase 18 verification protocol).
 */
export default function AslFeedbackBorder() {
  const { isAsl } = useI18n();
  const [pulse, setPulse] = useState(null); // { type: "success"|"error", key: number }
  const keyRef = useRef(0);

  const handle = useCallback((e) => {
    const detail = e && e.detail ? e.detail : {};
    const type = detail.type === "error" ? "error" : "success";
    keyRef.current += 1;
    setPulse({ type, key: keyRef.current });
  }, []);

  useEffect(() => {
    window.addEventListener("fm:asl_feedback", handle);
    return () => window.removeEventListener("fm:asl_feedback", handle);
  }, [handle]);

  // Non-ASL users never see the border, even if the event fires.
  if (!isAsl || !pulse) return null;

  const color = pulse.type === "success" ? "#39FF14" : "#DC143C";

  return (
    <div
      key={pulse.key}
      data-testid="asl-feedback-border"
      data-pulse-type={pulse.type}
      className="asl-feedback-pulse pointer-events-none fixed inset-0"
      style={{
        zIndex: 55,
        boxShadow: `inset 0 0 0 6px ${color}`,
        animationName: "asl-pulse",
        animationDuration: "800ms",
        animationTimingFunction: "ease-in-out",
        animationIterationCount: 1,
        animationFillMode: "forwards",
      }}
      onAnimationEnd={() => setPulse(null)}
      aria-hidden="true"
    />
  );
}
